import React, {
  useContext, lazy, Suspense
} from 'react';
import {
  Alert, Button, DrawerBody, DrawerFooter, Link, Typography
} from '@one-thd/sui-atomic-components';
import proptypes from 'prop-types';
import { useConfigService } from '@thd-nucleus/experience-context';
import { extend } from '@thd-nucleus/data-sources';
import { AddToCoreContext } from '../../core/AddToCoreContext';
import THDStorageUtils, { SESSION_STORAGE } from '@thd-olt-global/thd-storage-utils';
import { AddToProvider } from '../../AddToContext';
import { normalizePageType } from '../../AddToHelpers';

// Import only the dataModel for extend() - component is lazy loaded
import { AIListBuilder as AIListBuilderModule } from '../../common/ai-list-builder/AIListBuilder';

const AIListBuilder = lazy(() => (
  import('../../common/ai-list-builder/AIListBuilder').then((module) => ({
    default: module.AIListBuilder
  }))
));

const { string, func, shape, arrayOf } = proptypes;

const AddToListSuccess = ({
  newListDetails,
  onClose,
  products = []
}) => {
  const { selectedListId, selectedListName } = useContext(AddToCoreContext);

  const aiSuccessScreenEnabled = useConfigService('fs:aiListBuilderSuccessScreenEnabled');
  const showAiPrompt = !!aiSuccessScreenEnabled;

  const listName = selectedListName || newListDetails.listName;
  const listId = selectedListId || newListDetails.listId;

  const handleAiGenerate = (prompt) => {
    const rawPageType = typeof window !== 'undefined'
      ? window.digitalData?.page?.pageInfo?.pageType
      : null;
    const pageType = normalizePageType(rawPageType);

    try {
      THDStorageUtils.set(SESSION_STORAGE, {
        key: `lbai_prompt_${listId}`,
        value: JSON.stringify({ prompt, listId, pageType })
      });
    } catch (err) {
      // Graceful degradation: navigate anyway, just without auto-fill
    }
    window.location.href = `/list/view/details/${listId}`;
  };

  // Determine the correct count for pluralization
  const itemCount = products.length;
  const itemText = itemCount > 1 ? 'items have' : 'item has';

  return (
    <>
      <DrawerBody>
        <Alert status="success" size="large">
          <Typography>
            Your {itemText} been added to your list:&nbsp;
            <Link title={listName} href={`/list/view/details/${listId}`} weight="bold">
              {listName}
            </Link>
          </Typography>
        </Alert>

        {showAiPrompt && (
          <AddToProvider>
            <Suspense fallback={null}>
              <div className="sui-mt-4">
                <AIListBuilder
                  onGenerate={handleAiGenerate}
                  title="Complete your list with AI"
                  description={(
                    'Describe your project or paste a list of materials. '
                    + 'We\'ll use your input to add all the items you might need to your list!'
                  )}
                />
              </div>
            </Suspense>
          </AddToProvider>
        )}
      </DrawerBody>
      <DrawerFooter>
        <Button
          fullWidth
          variant="secondary"
          onClick={() => { onClose('ok'); }}
        >
          Close
        </Button>
      </DrawerFooter>
    </>
  );
};

AddToListSuccess.propTypes = {
  newListDetails: shape({
    listId: string,
    listName: string
  }).isRequired,
  onClose: func.isRequired,
  products: arrayOf(shape({
    itemId: string,
    quantity: string
  }))
};

AddToListSuccess.defaultProps = {
  products: []
};

AddToListSuccess.displayName = 'AddToListSuccess';

AddToListSuccess.dataModel = extend(AIListBuilderModule.dataModel);

export { AddToListSuccess };
